import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import './ToolPage.css'
import './PlanningPoker.css'
import PokerSeat from './PokerSeat'
import {
  joinPlanningPokerRoom,
  castPlanningPokerVote,
  fetchPlanningPokerState,
  planningPokerStreamUrl,
} from './utils/planningPokerApiClient'
import { averageVotes } from './utils/planningPokerAverage'
import { trackEvent } from '../../lib/analytics'
import { useDocumentMeta } from '../../hooks/useDocumentMeta'

const participantKey = (roomId) => `planning-poker-room-${roomId}-participant`
const voteKey = (roomId) => `planning-poker-room-${roomId}-vote`

const PlanningPokerJoin = () => {
  useDocumentMeta({
    title: 'Planning Poker | jarder.dev',
    description: 'Entre na sala e vote sem ver o que os outros escolheram até a revelação.',
  })

  const { id } = useParams()
  const [participantId, setParticipantId] = useState(() => localStorage.getItem(participantKey(id)))
  const [myVote, setMyVote] = useState(() => localStorage.getItem(voteKey(id)))
  const [name, setName] = useState('')
  const [state, setState] = useState(null)
  const [status, setStatus] = useState('checking')
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false

    fetchPlanningPokerState(id)
      .then((data) => {
        if (cancelled) return
        setState(data)
        if (!participantId) {
          setStatus('needsName')
          return
        }
        const mine = data.participants.find((p) => p.id === participantId)
        if (mine) {
          setStatus('ready')
        } else {
          localStorage.removeItem(participantKey(id))
          localStorage.removeItem(voteKey(id))
          setStatus('removed')
        }
      })
      .catch(() => {
        if (!cancelled) setStatus('notfound')
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  useEffect(() => {
    if (status !== 'ready') return undefined
    const eventSource = new EventSource(planningPokerStreamUrl(id))
    eventSource.onmessage = (event) => {
      const data = JSON.parse(event.data)
      setState(data)

      const mine = data.participants.find((p) => p.id === participantId)
      if (!mine) {
        localStorage.removeItem(participantKey(id))
        localStorage.removeItem(voteKey(id))
        setStatus('removed')
        return
      }
      // A new round clears everyone's vote server-side — sync the local
      // echo (the server never tells a participant their own vote back
      // before reveal, so this is the only place we track it).
      if (!mine.voted) {
        setMyVote(null)
        localStorage.removeItem(voteKey(id))
      }
    }
    return () => eventSource.close()
  }, [id, status, participantId])

  const join = async () => {
    const trimmed = name.trim()
    if (!trimmed) {
      setError('Digite seu nome.')
      return
    }
    setError('')
    try {
      const result = await joinPlanningPokerRoom(id, trimmed)
      localStorage.setItem(participantKey(id), result.participantId)
      setParticipantId(result.participantId)
      setStatus('ready')
      trackEvent('tool_generate', { tool: 'planning_poker_join' })
    } catch {
      setError('Não foi possível entrar na sala. Tente novamente.')
    }
  }

  const vote = async (card) => {
    setError('')
    try {
      await castPlanningPokerVote(id, participantId, card)
      setMyVote(card)
      localStorage.setItem(voteKey(id), card)
      trackEvent('tool_generate', { tool: 'planning_poker_vote' })
    } catch (err) {
      setError(err.message || 'Não foi possível votar.')
    }
  }

  if (status === 'checking') {
    return (
      <div className="tool-page">
        <p>Carregando...</p>
      </div>
    )
  }

  if (status === 'notfound') {
    return (
      <div className="tool-page">
        <Link to="/ferramentas" className="tool-back-link">
          ← Todas as ferramentas
        </Link>
        <h1>Sala não encontrada</h1>
        <p className="tool-disclaimer">Essa sala não existe ou expirou.</p>
      </div>
    )
  }

  if (status === 'removed') {
    return (
      <div className="tool-page">
        <Link to="/ferramentas" className="tool-back-link">
          ← Todas as ferramentas
        </Link>
        <h1>Você foi removido da sala</h1>
        <p className="tool-disclaimer">
          O mestre da sala removeu você da partida. Recarregue a página
          pra entrar de novo.
        </p>
        <button type="button" className="tool-button" onClick={() => window.location.reload()}>
          Recarregar
        </button>
      </div>
    )
  }

  if (status === 'needsName') {
    return (
      <div className="tool-page">
        <Link to="/ferramentas" className="tool-back-link">
          ← Todas as ferramentas
        </Link>
        <h1>Entrar no Planning Poker</h1>
        <p className="tool-disclaimer">Digite seu nome pra entrar na sala.</p>

        <div className="tool-field">
          <label htmlFor="participant-name">Seu nome</label>
          <input
            id="participant-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && join()}
          />
        </div>

        {error && <p className="tool-error">{error}</p>}

        <button type="button" className="tool-button" onClick={join}>
          Entrar
        </button>
      </div>
    )
  }

  const avg = state.revealed ? averageVotes(state.participants) : null

  return (
    <div className="tool-page">
      <Link to="/ferramentas" className="tool-back-link">
        ← Todas as ferramentas
      </Link>
      <h1>Planning Poker</h1>
      <p className="tool-disclaimer">
        Escolha uma carta. Ninguém vê o que você votou até o mestre
        revelar pra todo mundo junto.
      </p>

      {state.topic && <p className="poker-topic-display">{state.topic}</p>}

      {error && <p className="tool-error">{error}</p>}

      <div className="poker-cards">
        {state.cards.map((card) => (
          <button
            key={card}
            type="button"
            className={`poker-card${myVote === card ? ' selected' : ''}`}
            onClick={() => vote(card)}
            disabled={state.revealed}
          >
            {card}
          </button>
        ))}
      </div>

      {!state.revealed && (
        <p className="poker-waiting-hint">
          {myVote ? 'Voto registrado. Aguardando os outros...' : 'Escolha uma carta acima.'}
        </p>
      )}

      {avg !== null && <p className="poker-average">Média: {avg}</p>}

      <h2 className="poker-participants-heading">
        Participantes ({state.participants.length})
      </h2>

      <div className="poker-table">
        {state.participants.map((participant) => (
          <PokerSeat key={participant.id} participant={participant} revealed={state.revealed} />
        ))}
      </div>
    </div>
  )
}

export default PlanningPokerJoin
